import React from "react";
import { Link } from "react-router-dom";
import { useConsent } from "../contexts/ConsentContext";
import { useLanguage } from "../contexts/LanguageContext";

const copy = {
  it: {
    eyebrow: "Informativa sul trattamento dei dati personali",
    title: "Privacy Policy",
    updated: "Ultimo aggiornamento: 9 settembre 2026",
    intro:
      "Questa informativa descrive come vengono trattati i dati personali quando visiti evatazzari.com, invii un messaggio o una richiesta relativa alle opere.",
    controllerTitle: "1. Titolare del trattamento",
    controller: "Il titolare del trattamento è Eva Tazzari.",
    contactLabel: "Contatto privacy:",
    dataTitle: "2. Dati trattati e finalità",
    dataItems: [
      {
        title: "Navigazione e sicurezza",
        text: "Il sito e i servizi che lo rendono disponibile possono trattare dati tecnici necessari a consegnare le pagine, mantenere la sicurezza e diagnosticare malfunzionamenti, come indirizzo IP, data e ora della richiesta, pagina richiesta, browser e dispositivo. Cloudflare Turnstile tratta inoltre segnali tecnici quando completi la verifica antispam dei moduli.",
      },
      {
        title: "Modulo contatti",
        text: "Quando scrivi tramite il modulo vengono trattati nome, cognome, indirizzo email, messaggio, conferma del consenso e dati tecnici necessari a verificare e inoltrare la richiesta. Il modulo non comporta l’iscrizione a una newsletter.",
      },
      {
        title: "Richieste T.I.N.D.E.R.",
        text: "Quando invii una richiesta vengono trattati nome, indirizzo email, tipo di richiesta, eventuale riferimento a un’opera, messaggio e dati tecnici della verifica antispam.",
      },
      {
        title: "Statistiche facoltative",
        text: "Solo se presti il consenso, Google Analytics misura visite e pagine consultate e PostHog produce statistiche pseudonime sull’uso delle pagine. PostHog è configurato senza cattura automatica delle interazioni e senza registrazione delle sessioni.",
      },
      {
        title: "Preferenze tecniche",
        text: "Il browser conserva la lingua, la visualizzazione dell’introduzione nella sessione e la scelta sulle tecnologie facoltative. I dettagli sono nella Cookie Policy.",
      },
    ],
    legalTitle: "3. Basi giuridiche",
    legal:
      "I dati necessari a rispondere a messaggi e richieste sono trattati sulla base del consenso espresso nel modulo e, quando la richiesta riguarda una possibile collaborazione o acquisizione, per svolgere attività precontrattuali richieste dall’interessato. I trattamenti tecnici strettamente necessari si fondano sulla necessità di erogare e proteggere il sito e sul legittimo interesse alla sua sicurezza. Google Analytics e PostHog si attivano esclusivamente sulla base del consenso, che è facoltativo e revocabile.",
    recipientsTitle: "4. Destinatari e fornitori",
    recipients:
      "I dati sono accessibili a Eva Tazzari e, nei limiti necessari al servizio svolto, ai fornitori tecnici. Il flusso del modulo contatti utilizza Brevo per la gestione o l’inoltro del messaggio e MongoDB per la gestione dei dati nel backend. Cloudflare fornisce la verifica antispam; Google e PostHog ricevono dati soltanto per le rispettive categorie di analytics autorizzate. Possono inoltre intervenire i fornitori di hosting e manutenzione. I dati non sono venduti e i moduli non vengono usati per iscrivere l’utente a comunicazioni promozionali.",
    transfersTitle: "5. Trasferimenti internazionali",
    transfers:
      "Alcuni fornitori, in particolare Google, PostHog e Cloudflare, operano tramite infrastrutture e società situate anche fuori dallo Spazio economico europeo. Localizzazione del trattamento e garanzie applicabili dipendono dal servizio e dalla configurazione in uso. Puoi chiedere informazioni sul meccanismo di trasferimento applicabile scrivendo al contatto privacy; le informative dei fornitori sono collegate in fondo a questa pagina.",
    retentionTitle: "6. Conservazione",
    retention:
      "I dati dei messaggi vengono conservati per il tempo necessario a rispondere e gestire l’eventuale seguito della richiesta; successivamente sono mantenuti solo quando occorre adempiere obblighi di legge o tutelare diritti in caso di contestazione. I dati tecnici e di sicurezza seguono i tempi necessari a garantire il servizio, prevenire abusi e diagnosticare problemi. Le durate delle tecnologie memorizzate nel browser sono riportate nella Cookie Policy.",
    rightsTitle: "7. Diritti dell’interessato",
    rights:
      "Puoi chiedere accesso, rettifica, cancellazione, limitazione o portabilità dei dati e opporti al trattamento nei casi previsti. Puoi revocare il consenso in qualsiasi momento; la revoca non pregiudica la liceità del trattamento effettuato prima della revoca. Per esercitare i diritti scrivi a info@evatazzari.com. Puoi inoltre proporre reclamo all’autorità di controllo competente, inclusa l’Agencia Española de Protección de Datos (AEPD).",
    choicesTitle: "8. Cookie e preferenze",
    choices:
      "Puoi consultare l’inventario delle tecnologie e modificare in ogni momento le categorie facoltative. Il rifiuto non limita l’accesso alle pagine del sito.",
    cookieLink: "Consulta la Cookie Policy",
    settings: "Riapri le impostazioni cookie",
    providersTitle: "Informative dei principali fornitori",
    providerLinks: {
      google: "Informativa privacy di Google",
      posthog: "Informativa privacy di PostHog",
      cloudflare: "Informativa privacy di Cloudflare",
      aepd: "Agencia Española de Protección de Datos",
    },
  },
  en: {
    eyebrow: "Personal data processing notice",
    title: "Privacy Policy",
    updated: "Last updated: 9 September 2026",
    intro:
      "This notice explains how personal data is processed when you visit evatazzari.com, send a message or make an enquiry about the works.",
    controllerTitle: "1. Data controller",
    controller: "The data controller is Eva Tazzari.",
    contactLabel: "Privacy contact:",
    dataTitle: "2. Data processed and purposes",
    dataItems: [
      {
        title: "Browsing and security",
        text: "The website and the services that make it available may process technical data needed to deliver pages, maintain security and diagnose faults, such as IP address, request date and time, requested page, browser and device. Cloudflare Turnstile also processes technical signals when you complete the forms’ anti-spam check.",
      },
      {
        title: "Contact form",
        text: "When you write through the form, your first and last name, email address, message, consent confirmation and the technical data needed to verify and forward the request are processed. The form does not subscribe you to a newsletter.",
      },
      {
        title: "T.I.N.D.E.R. enquiries",
        text: "When you submit an enquiry, your name, email address, type of request, any selected artwork reference, message and technical anti-spam verification data are processed.",
      },
      {
        title: "Optional analytics",
        text: "Only if you consent, Google Analytics measures visits and pages viewed, while PostHog produces pseudonymous statistics about page usage. PostHog is configured without automatic interaction capture and without session recording.",
      },
      {
        title: "Technical preferences",
        text: "Your browser stores the chosen language, whether the introduction has been shown during the session and your choice about optional technologies. Details are provided in the Cookie Policy.",
      },
    ],
    legalTitle: "3. Lawful bases",
    legal:
      "Data needed to answer messages and enquiries is processed on the basis of the consent given in the form and, where an enquiry concerns a possible collaboration or acquisition, to take pre-contractual steps requested by you. Strictly necessary technical processing is based on the need to provide and protect the website and on the legitimate interest in its security. Google Analytics and PostHog are activated solely on the basis of consent, which is optional and may be withdrawn.",
    recipientsTitle: "4. Recipients and service providers",
    recipients:
      "Data is accessible to Eva Tazzari and, only as required to perform their services, technical providers. The contact form flow uses Brevo to manage or forward the message and MongoDB to manage data in the backend. Cloudflare supplies the anti-spam check; Google and PostHog receive data only for the respective analytics categories you authorise. Hosting and maintenance providers may also be involved. Data is not sold, and forms are not used to subscribe you to promotional messages.",
    transfersTitle: "5. International transfers",
    transfers:
      "Some providers, particularly Google, PostHog and Cloudflare, operate through infrastructure and companies located outside the European Economic Area as well. The processing location and applicable safeguards depend on the service and configuration in use. You may ask for information about the applicable transfer mechanism by writing to the privacy contact; provider notices are linked at the end of this page.",
    retentionTitle: "6. Retention",
    retention:
      "Message data is kept for as long as needed to answer and handle any follow-up to the enquiry; afterwards it is retained only where required to comply with legal obligations or protect rights in the event of a dispute. Technical and security data follows the periods needed to provide the service, prevent abuse and diagnose faults. Browser technology lifetimes are listed in the Cookie Policy.",
    rightsTitle: "7. Your rights",
    rights:
      "You may request access, rectification, erasure, restriction or portability of your data and object to processing where provided by law. You may withdraw consent at any time; withdrawal does not affect the lawfulness of processing carried out beforehand. To exercise your rights, write to info@evatazzari.com. You may also lodge a complaint with the competent supervisory authority, including the Spanish Data Protection Agency (AEPD).",
    choicesTitle: "8. Cookies and preferences",
    choices:
      "You can consult the technology inventory and change optional categories at any time. Rejecting them does not restrict access to the website’s pages.",
    cookieLink: "Read the Cookie Policy",
    settings: "Reopen cookie settings",
    providersTitle: "Main providers’ privacy notices",
    providerLinks: {
      google: "Google Privacy Policy",
      posthog: "PostHog Privacy Policy",
      cloudflare: "Cloudflare Privacy Policy",
      aepd: "Spanish Data Protection Agency",
    },
  },
};

export default function PrivacyPolicy() {
  const { language } = useLanguage();
  const { openPreferences } = useConsent();
  const content = copy[language] || copy.it;

  return (
    <article className="legal-page">
      <header className="legal-page-header">
        <p className="small-text">{content.eyebrow}</p>
        <h1>{content.title}</h1>
        <p className="small-text">{content.updated}</p>
        <p className="legal-page-lead">{content.intro}</p>
      </header>

      <div>
        <section className="legal-section">
          <h2>{content.controllerTitle}</h2>
          <p>{content.controller}</p>
          <p>
            {content.contactLabel}{" "}
            <a href="mailto:info@evatazzari.com">info@evatazzari.com</a>
          </p>
        </section>

        <section className="legal-section">
          <h2>{content.dataTitle}</h2>
          {content.dataItems.map((item) => (
            <div key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </section>

        {[
          [content.legalTitle, content.legal],
          [content.recipientsTitle, content.recipients],
          [content.transfersTitle, content.transfers],
          [content.retentionTitle, content.retention],
          [content.rightsTitle, content.rights],
        ].map(([title, text]) => (
          <section className="legal-section" key={title}>
            <h2>{title}</h2>
            <p>{text}</p>
          </section>
        ))}

        <section className="legal-section">
          <h2>{content.choicesTitle}</h2>
          <p>{content.choices}</p>
          <div className="legal-actions">
            <Link to={`/${language}/cookies`}>
              {content.cookieLink}
            </Link>
            <button type="button" onClick={openPreferences}>
              {content.settings}
            </button>
          </div>
        </section>

        <section className="legal-section">
          <h2>{content.providersTitle}</h2>
          <ul>
            <li>
              <a className="legal-inline-link" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.google}
              </a>
            </li>
            <li>
              <a className="legal-inline-link" href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.posthog}
              </a>
            </li>
            <li>
              <a className="legal-inline-link" href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.cloudflare}
              </a>
            </li>
            <li>
              <a className="legal-inline-link" href="https://www.aepd.es/" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.aepd}
              </a>
            </li>
          </ul>
        </section>
      </div>
    </article>
  );
}
